
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://newerp.awasthivitality.com/api";
const PINCODE_API = "https://api.postalpincode.in/pincode";

const SmartCourierForm = () => {
    const navigate = useNavigate();
    const { patient_id: routePatientId } = useParams();
    const dropdownRef = useRef(null);
    const pinRequestRef = useRef(0);

    const [couriers, setCouriers] = useState([]);
    const [patients, setPatients] = useState([]);
    const [courses, setCourses] = useState([]);
    const [search, setSearch] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [pinLoading, setPinLoading] = useState(false);
    const [courierCheckResult, setCourierCheckResult] = useState(null);
    const [courierCheckLoading, setCourierCheckLoading] = useState(false);
    const [selectedPartnerId, setSelectedPartnerId] = useState("");


    const [data, setData] = useState({
        patient_id: routePatientId || "",
        mop: "ONLINE",
        course: "",
        delivery_courceid: "",
        width: "",
        height: "",
        length: "",
        weight: "",
        amount: "",
        zipcode: "",
        address: "",
        mobile_no: "",
        city: "",
        state: "",
        relative_name: "",
        order_reference: ""
    });

    // Fetch Patients & Couriers
    useEffect(() => {
        const token = (localStorage.getItem("session_token") || "").trim();

        const headers = {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(token ? { "X-Session-Token": token } : {})
        };

        fetch(`${API_BASE}/patients?page=1&per_page=100`, { headers })
            .then(res => res.json())
            .then(json => {
                const plist = Array.isArray(json?.data?.patients?.data)
                    ? json.data.patients.data
                    : Array.isArray(json?.data?.patients)
                    ? json.data.patients
                    : Array.isArray(json?.data)
                    ? json.data
                    : [];

                setPatients(plist);
            })
            .catch(err => console.error("Patient loading failed:", err));

        fetch(`${API_BASE}/couriers`, { headers })
            .then(res => res.json())
            .then(json => {
                const list = json?.data || json || [];
                setCouriers(Array.isArray(list) ? list : []);
            })
            .catch(err => console.error("Courier loading failed:", err));
    }, []);

    // Load courses for selected patient
    useEffect(() => {
        const loadCourses = async () => {
            if (!data.patient_id) {
                setCourses([]);
                return;
            }

            try {
                const token = (localStorage.getItem("session_token") || "").trim();

                const headers = {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    ...(token ? { "X-Session-Token": token } : {})
                };

                const viewUrl =
                    `${API_BASE}/patient/view/${data.patient_id}` +
                    (token ? `?token=${encodeURIComponent(token)}` : "");

                let resp = await fetch(viewUrl, { headers });

                if (!resp.ok) {
                    resp = await fetch(
                        `${API_BASE}/patient/details/${data.patient_id}`,
                        { headers }
                    );
                }

                if (!resp.ok) {
                    throw new Error("Unable to load patient courses");
                }

                const json = await resp.json();
                const d = json?.data || {};

                const list =
                    d.patient_courses || d.courses || d.course_data || [];

                const normalized = [];

                list.forEach(c => {
                    const id = c.patient_course_id ?? c.id ?? c.course_id;
                    const name =
                        c.diagnosis || c.diagnosis_name || c.name;
                    const diagId =
                        c.diagnosis_id ?? c.diagnosis_code_id;

                    if (id) {
                        normalized.push({
                            patient_course_id: String(id),
                            diagnosis_id: String(diagId || ""),
                            diagnosis_label: name || `Course ${id}`
                        });
                    }
                });

                setCourses(normalized);

                if (normalized.length > 0) {
                    setData(prev => ({
                        ...prev,
                        course: normalized[0].diagnosis_id,
                        delivery_courceid:
                            normalized[0].patient_course_id
                    }));
                }
            } catch (err) {
                console.error("Course loading failed:", err);
            }
        };

        loadCourses();
    }, [data.patient_id]);


    ///// Courier Check API function add karein
    const checkCourierServiceability = async (pincode) => {
    setCourierCheckLoading(true);
    setCourierCheckResult(null);

    try {
        const payload = {
            width: data.width,
            height: data.height,
            length: data.length,
            weight: data.weight,
            amount: data.amount,
            zipcode: pincode,
            mop: data.mop
        };

        const response = await fetch(
            "https://newerp.awasthivitality.com/api/courier-check",
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json"
                },
                body: JSON.stringify(payload)
            }
        );

        const result = await response.json();

        setCourierCheckResult({
            success: response.ok,
            data: result
        });
    } catch (error) {
        console.error("Courier Check API Error:", error);

        setCourierCheckResult({
            success: false,
            data: {
                message: "Unable to check courier serviceability."
            }
        });
    } finally {
        setCourierCheckLoading(false);
    }
};

    // Handle input changes and automatically fetch City/State by PIN code
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name !== "zipcode") {
            setData(prev => ({
                ...prev,
                [name]: value
            }));
            return;
        }

        // Allow only 6 numeric digits
        const pincode = value.replace(/\D/g, "").slice(0, 6);

        // Invalidate any previous PIN lookup
        const requestId = ++pinRequestRef.current;

        setData(prev => ({
            ...prev,
            zipcode: pincode,
            city: "",
            state: ""
        }));

        if (pincode.length !== 6) {
            setPinLoading(false);
            return;
        }

        const fetchPincodeDetails = async () => {
            setPinLoading(true);

            try {
                const response = await fetch(
                    `${PINCODE_API}/${pincode}`
                );

                if (!response.ok) {
                    throw new Error("PIN code API request failed");
                }

                const result = await response.json();

                // Ignore outdated responses
                if (requestId !== pinRequestRef.current) {
                    return;
                }

                if (
                    result?.[0]?.Status === "Success" &&
                    result[0]?.PostOffice?.length > 0
                ) {
                    const office = result[0].PostOffice[0];

setData(prev => ({
    ...prev,
    zipcode: pincode,
    city: office.District || "",
    state: office.State || ""
}));

// City/State fetch hone ke baad Courier Check API call
checkCourierServiceability(pincode);

                } else {
                    setData(prev => ({
                        ...prev,
                        city: "",
                        state: ""
                    }));

                    toast.error(
                        "Invalid PIN code. Please check the PIN code."
                    );
                }
            } catch (err) {
                if (requestId === pinRequestRef.current) {
                    console.error("PIN code lookup failed:", err);
                    toast.error(
                        "Unable to fetch City/State. Please try again."
                    );
                }
            } finally {
                if (requestId === pinRequestRef.current) {
                    setPinLoading(false);
                }
            }
        };

        fetchPincodeDetails();
    };

    // Handle course selection
    const handleCourseSelect = (e) => {
        const selectedId = e.target.value;

        const selected = courses.find(
            c => String(c.patient_course_id) === String(selectedId)
        );

        setData(prev => ({
            ...prev,
            delivery_courceid: selectedId,
            course: selected?.diagnosis_id || ""
        }));
    };

    // Submit courier entry
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (data.zipcode && data.zipcode.length !== 6) {
            toast.error("Please enter a valid 6-digit PIN code.");
            return;
        }

        const fd = new FormData();

        fd.append("patient_id", data.patient_id);
        fd.append("delivery_courceid", data.delivery_courceid);
        fd.append("mop", data.mop);
        fd.append("width", data.width);
        fd.append("course", data.course);
        fd.append("height", data.height);
        fd.append("length", data.length);
        fd.append("weight", data.weight);
        fd.append("amount", data.amount);
        fd.append("zipcode", data.zipcode);
        fd.append("address", data.address);
        fd.append("mobile_no", data.mobile_no);
        fd.append("city", data.city);
        fd.append("state", data.state);
        fd.append("relative_name", data.relative_name);
        fd.append("order_reference", data.order_reference);
        fd.append("partnerId", selectedPartnerId);

        fd.append(
            "delivery_date",
            new Date().toISOString().split("T")[0]
        );
        fd.append(
            "payment_status",
            data.mop === "COD" ? "NOT PAID" : "PAID"
        );
        fd.append("status", "UNDELIVERED");
        fd.append("delivery_status", "Enabled");

        console.log("===== FORM DATA =====");
        for (const [key, value] of fd.entries()) {
            console.log(key, ":", value);
        }
        console.log("=====================");

        try {
            const token = (
                localStorage.getItem("session_token") || ""
            ).trim();

            const res = await fetch(`${API_BASE}/apexdeliveries`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "X-Session-Token": token
                },
                body: fd
            });

            const responseText = await res.text();
            console.log("RAW RESPONSE:", responseText);

            let errorMsg = "Failed to save entry";

            try {
                const parsed = JSON.parse(responseText);

                if (parsed.message) {
                    try {
                        const inner = JSON.parse(parsed.message);
                        errorMsg =
                            inner.data?.[0] ||
                            inner.Message ||
                            parsed.message;
                    } catch {
                        errorMsg = parsed.message;
                    }
                } else if (parsed.error) {
                    errorMsg = parsed.error;
                }
            } catch {
                if (responseText) {
                    errorMsg = responseText;
                }
            }

            if (res.ok) {
                toast.success("Entry Saved Successfully");

                setTimeout(() => {
                    navigate("/CourierEntryList");
                }, 1500);
            } else {
                toast.error(errorMsg);
            }
        } catch (err) {
            console.error("Submit error:", err);
            toast.error("Network Error");
        }
    };

    // Search patients
    const searchPatients = async (query) => {
        try {
            const q = query.trim();

            if (!q) {
                setPatients([]);
                return;
            }

            const token = (
                localStorage.getItem("session_token") || ""
            ).trim();

            const headers = {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...(token ? { "X-Session-Token": token } : {})
            };

            const res = await fetch(
                `${API_BASE}/patients?search=${encodeURIComponent(q)}&per_page=20`,
                { headers }
            );

            if (!res.ok) {
                throw new Error("Patient search request failed");
            }

            const json = await res.json();

            const rawList =
                json?.data?.patients?.data ||
                json?.data?.patients ||
                json?.data ||
                [];

            const list = Array.isArray(rawList) ? [...rawList] : [];
            const ql = q.toLowerCase();

            list.sort((a, b) => {
                const astr =
                    `${a.pr_no || ""} ${a.patient_id || ""} ${a.name || ""}`
                        .toLowerCase();

                const bstr =
                    `${b.pr_no || ""} ${b.patient_id || ""} ${b.name || ""}`
                        .toLowerCase();

                if (astr.startsWith(ql) && !bstr.startsWith(ql)) {
                    return -1;
                }

                if (bstr.startsWith(ql) && !astr.startsWith(ql)) {
                    return 1;
                }

                if (astr.includes(ql) && !bstr.includes(ql)) {
                    return -1;
                }

                if (bstr.includes(ql) && !astr.includes(ql)) {
                    return 1;
                }

                return 0;
            });

            setPatients(list);
        } catch (err) {
            console.error("Patient search failed:", err);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const closeDropdown = (e) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", closeDropdown);

        return () => {
            document.removeEventListener("mousedown", closeDropdown);
        };
    }, []);

    const getPatientLabel = (p) => {
        if (!p) return "";

        return `${p.pr_no || p.patient_id} / ${p.name}${
            p.mobile ? " / " + p.mobile : ""
        }`;
    };

    return (
        <div className="scf-page">
            <header className="scf-header">
                🚚 Courier Booking Form
            </header>

            <form className="scf-card" onSubmit={handleSubmit}>
                {/* Patient, Course and Payment */}
                <div
                    className="scf-row-layout"
                    style={{ gridTemplateColumns: "1fr 1fr 1fr" }}
                >
                    <div
                        className="scf-group"
                        style={{ position: "relative" }}
                        ref={dropdownRef}
                    >
                        <label className="scf-label">
                            Select Patient <span className="req">*</span>
                        </label>

                        <input
                            type="text"
                            placeholder="Search Patient ID / PR No / Name / Mobile"
                            className="scf-input"
                            value={
                                search !== ""
                                    ? search
                                    : getPatientLabel(selectedPatient)
                            }
                            onChange={(e) => {
                                const value = e.target.value;

                                setSearch(value);
                                setShowDropdown(true);

                                if (value === "") {
                                    setSelectedPatient(null);
                                    setData(prev => ({
                                        ...prev,
                                        patient_id: "",
                                        delivery_courceid: "",
                                        course: ""
                                    }));
                                    setCourses([]);
                                } else {
                                    searchPatients(value);
                                }
                            }}
                            onFocus={() => setShowDropdown(true)}
                            required
                        />

                        {showDropdown && (
                            <div className="scf-dropdown">
                                {patients.map(p => (
                                    <div
                                        key={p.patient_id}
                                        className="scf-dropdown-item"
                                        onClick={() => {
                                            setSelectedPatient(p);

                                            setData(prev => ({
                                                ...prev,
                                                patient_id: String(p.patient_id),
                                                delivery_courceid: "",
                                                course: ""
                                            }));

                                            setCourses([]);
                                            setSearch("");
                                            setShowDropdown(false);
                                        }}
                                    >
                                        {p.pr_no} / {p.name}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">
                            Course <span className="req">*</span>
                        </label>

                        <select
                            name="delivery_courceid"
                            value={data.delivery_courceid}
                            onChange={handleCourseSelect}
                            className="scf-select"
                            required
                        >
                            <option value="">Select Course</option>

                            {courses.map(c => (
                                <option
                                    key={c.patient_course_id}
                                    value={c.patient_course_id}
                                >
                                    {c.diagnosis_label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">
                            Mode of Payment <span className="req">*</span>
                        </label>

                        <select
                            name="mop"
                            value={data.mop}
                            onChange={handleChange}
                            className="scf-select"
                            required
                        >
                            <option value="ONLINE">ONLINE</option>
                            <option value="COD">COD</option>
                        </select>
                    </div>
                </div>

                {/* Package and Delivery Details */}
                <div className="scf-row-layout">
                    <div className="scf-group">
                        <label className="scf-label">Amount <span className="req">*</span></label>
                        <input
                            type="text"
                            name="amount"
                            value={data.amount}
                            onChange={handleChange}
                            className="scf-input"
                            required
                        />
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">Width (cm) <span className="req">*</span> </label>
                        <input
                            type="number"
                            name="width"
                            value={data.width}
                            onChange={handleChange}
                            className="scf-input"
                            required
                        />
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">Height (cm) <span className="req">*</span></label>
                        <input
                            type="number"
                            name="height"
                            value={data.height}
                            onChange={handleChange}
                            className="scf-input"
                            required
                        />
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">Length (cm) <span className="req">*</span></label>
                        <input
                            type="number"
                            name="length"
                            value={data.length}
                            onChange={handleChange}
                            className="scf-input"
                            required
                        />
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">Weight (g) <span className="req">*</span></label>
                        <input
                            type="number"
                            name="weight"
                            value={data.weight}
                            onChange={handleChange}
                            className="scf-input"
                            required
                        />
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">Relative Name  (Optional)</label>
                        <input
                            type="text"
                            name="relative_name"
                            value={data.relative_name}
                            onChange={handleChange}
                            className="scf-input"
                        />
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">
                            Mobile No <span className="req">*</span>
                        </label>
                        <input
                            type="text"
                            name="mobile_no"
                            value={data.mobile_no}
                            onChange={handleChange}
                            className="scf-input"
                            placeholder="Enter mobile number"
                            required
                        />
                    </div>

                    {/* PIN Code */}
                    <div className="scf-group">
                        <label className="scf-label">Zip Code / PIN Code <span className="req">*</span></label>
                        <input
                            type="text"
                            name="zipcode"
                            value={data.zipcode}
                            onChange={handleChange}
                            className="scf-input"
                            placeholder="Enter 6-digit PIN code"
                            maxLength={6}
                            inputMode="numeric"
                            required
                        />

                        {pinLoading && (
                            <small className="scf-hint">
                                Fetching City and State...
                            </small>
                        )}
                    </div>

                    {/* Auto-filled City */}
                    <div className="scf-group">
                        <label className="scf-label">City / District <span className="req">*</span></label>
                        <input
                            type="text"
                            name="city"
                            value={data.city}
                            className="scf-input"
                            placeholder="Auto-filled from PIN code"
                            required
                        />
                    </div>

                    {/* Auto-filled State */}
                    <div className="scf-group">
                        <label className="scf-label">State <span className="req">*</span></label>
                        <input
                            type="text"
                            name="state"
                            value={data.state}
                            className="scf-input"
                            placeholder="Auto-filled from PIN code"
                            required
                        />
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">Courier Address <span className="req">*</span></label>
                        <textarea
                            name="address"
                            value={data.address}
                            onChange={(e) => {
                                const value = e.target.value;
                                const words = value.trim()
                                    ? value.trim().split(/\s+/)
                                    : [];

                                if (words.length <= 20) {
                                    setData(prev => ({
                                        ...prev,
                                        address: value
                                    }));
                                } else {
                                    toast.error(
                                        "Maximum 20 words allowed"
                                    );
                                }
                            }}
                            className="scf-input"
                            placeholder="Enter delivery address (max 20 words)"
                            required
                            rows={1}
                        />
                    </div>

                    <div className="scf-group">
                        <label className="scf-label">
                            Order Reference <span className="req">*</span>
                        </label>
                        <input
                            type="text"
                            name="order_reference"
                            value={data.order_reference}
                            onChange={handleChange}
                            className="scf-input"
                            required
                        />
                    </div>
                </div>


                {/* Courier Check API Result */}
{/* Courier Check Result */}
{!courierCheckLoading && courierCheckResult && (
    <div className="scf-courier-result">
        <h4>Courier Check Result</h4>

        {(() => {
            const response = courierCheckResult.data;
            const carriers = response?.getCarriers || {};

            // Validation/API error
            const errorMessage =
                response?.message ||
                response?.error ||
                response?.errors;

            const carrierEntries = Object.entries(carriers);

            return (
                <>
                    {errorMessage && (
                        <div className="scf-api-error">
                            {typeof errorMessage === "string"
                                ? errorMessage
                                : JSON.stringify(errorMessage)}
                        </div>
                    )}

                    {carrierEntries.length > 0 ? (
                        <div className="scf-carrier-list">
                            {carrierEntries.map(([name, carrier]) => {
                                const item = carrier?.data?.[0];
                                const total = item?.charges?.Total;
                                const carrierError =
                                    carrier?.message !== "success" ||
                                    carrier?.status !== 200;

                                return (
                                    <div
                                        className="scf-carrier-item"
                                        key={name}
                                    >
                                    <div className="scf-carrier-name">
                                    <label className="scf-carrier-label">
                                    <input
                                    type="checkbox"
                                    name="selectedCourier"
                                    checked={
                                    String(selectedPartnerId) === String(item?.partnerId)
                                    }
                                    onChange={() => {
                                    setSelectedPartnerId(
                                    String(item?.partnerId)
                                    );
                                    }}
                                    />

                                    <span>{item?.carrier || name}</span>
                                    </label>
                                    </div>

                                        {carrierError ? (
                                            <div className="scf-api-error">
                                                {carrier?.message ||
                                                    "Unable to calculate courier charges"}
                                            </div>
                                        ) : item ? (
                                            <div className="scf-carrier-total">
                                                Total: ₹
                                                {Number(total || 0).toFixed(2)}
                                            </div>
                                        ) : (
                                            <div className="scf-api-error">
                                                No courier data available
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        !errorMessage && (
                            <div className="scf-api-error">
                                {response?.message ||
                                    "No courier details available"}
                            </div>
                        )
                    )}
                </>
            );
        })()}
    </div>
)}

{courierCheckLoading && (
    <div className="scf-courier-result">
        Checking courier charges...
    </div>
)}

<button type="submit" className="scf-button">
    Save Courier Entry
</button>


               
            </form>

            <ToastContainer
                position="top-right"
                theme="colored"
            />

            <style>{`
                * {
                    box-sizing: border-box;
                    font-family: sans-serif;
                }

                .scf-page {
                    min-height: 100vh;
                    padding: 20px;
                    background: #f4fbff;
                }

                @media (min-width: 992px) {
                    .scf-card,
                    .scf-header {
                        margin-left: 260px;
                    }
                }

                .scf-header {
                    text-align: center;
                    font-size: 24px;
                    color: #2fb2e7;
                    font-weight: bold;
                    margin-bottom: 20px;
                }

                .scf-card {
                    background: #fff;
                    padding: 25px;
                    border-radius: 12px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
                }

                .scf-group {
                    display: flex;
                    flex-direction: column;
                    margin-bottom: 12px;
                    min-width: 0;
                }

                .scf-label {
                    font-size: 13px;
                    font-weight: 600;
                    margin-bottom: 5px;
                    color: #555;
                }

                .scf-input,
                .scf-select {
                    width: 100%;
                    padding: 10px;
                    border-radius: 6px;
                    border: 1px solid #ddd;
                    outline: none;
                    font-size: 13px;
                    background: #fff;
                }

                .scf-input:read-only {
                    background: #f3f6f8;
                    color: #444;
                }

                .scf-hint {
                    margin-top: 5px;
                    color: #1685b0;
                    font-size: 12px;
                }

                .scf-row-layout {
                    display: grid;
                    grid-template-columns: repeat(4, minmax(0, 1fr));
                    gap: 15px;
                    margin-bottom: 10px;
                }

                .scf-button {
                    width: 220px;
                    max-width: 100%;
                    padding: 12px;
                    background: #2fb2e7;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-weight: bold;
                    cursor: pointer;
                    margin: 20px auto 0;
                    display: block;
                }

                .scf-button:hover {
                    background: #199dcc;
                }

                .scf-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: #fff;
                    border: 1px solid #ddd;
                    z-index: 10;
                    max-height: 200px;
                    overflow-y: auto;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
                }

                .scf-dropdown-item {
                    padding: 10px;
                    cursor: pointer;
                    border-bottom: 1px solid #eee;
                    font-size: 13px;
                }

                .scf-dropdown-item:hover {
                    background: #f0faff;
                }

                .req {
                    color: red;
                }

                @media (max-width: 991px) {
                    .scf-row-layout {
                        grid-template-columns: repeat(2, minmax(0, 1fr));
                    }

                    .scf-card,
                    .scf-header {
                        margin-left: 0;
                    }
                }

                @media (max-width: 600px) {
                    .scf-row-layout {
                        grid-template-columns: 1fr;
                    }

                    .scf-card {
                        padding: 16px;
                    }
                }

                .scf-courier-result {
    margin-top: 20px;
    width: 100%;
}

.scf-result-loading {
    padding: 15px;
    text-align: center;
    background: #eaf7ff;
    color: #0879a8;
    border: 1px solid #b9e6f8;
    border-radius: 8px;
    font-weight: 600;
}

.scf-result-box {
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 15px;
    overflow-wrap: anywhere;
}

.scf-result-success {
    background: #eaf8ef;
    border: 1px solid #a8dfb8;
    color: #176b35;
}

.scf-result-error {
    background: #fff0f0;
    border: 1px solid #f1b5b5;
    color: #b42318;
}

.scf-result-box h4 {
    margin: 0 0 12px;
    font-size: 16px;
}

.scf-result-box pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    margin: 0;
    font-size: 13px;
    font-family: monospace;
    color: inherit;
}

.scf-courier-result {
    width: 100%;
    margin: 20px 0;
    padding: 18px;
    background: #fff;
    border: 1px solid #dce5eb;
    border-radius: 10px;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.05);
}

.scf-courier-result h4 {
    margin: 0 0 15px;
    color: #263746;
    font-size: 17px;
    font-weight: 700;
}

.scf-carrier-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.scf-carrier-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    padding: 14px 16px;
    border: 1px solid #e2e8ee;
    border-radius: 8px;
    background: #f8fbfd;
    flex-wrap: wrap;
}

.scf-carrier-name {
    font-size: 14px;
    font-weight: 700;
    color: #34495e;
}

.scf-carrier-total {
    font-size: 15px;
    font-weight: 700;
    color: #168348;
}

.scf-api-error {
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #fff0f0;
    color: #c62828;
    font-size: 13px;
    overflow-wrap: anywhere;
}

.scf-carrier-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    margin: 0;
}

.scf-carrier-label input[type="checkbox"] {
    width: 17px;
    height: 17px;
    cursor: pointer;
    accent-color: #168348;
    flex-shrink: 0;
}

@media (max-width: 600px) {
    .scf-carrier-item {
        align-items: flex-start;
        flex-direction: column;
        gap: 8px;
    }
}

            `}</style>
        </div>
    );
};

export default SmartCourierForm;

<style>

    
</style>